import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { type ITabItem } from 'shared/ui'
import { type ISendFromInfo } from 'entities/SendFrom/api/type'
import { fetchSendFromInfo } from 'entities/SendFrom/lib/getSendFromInfo'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { TagsApi } from 'entities/Tags'
import { Tag } from 'entities/Tags/model/Tag'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { type RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import {
  AgentActionType,
  type IAgentActionSettings,
  type IAgentActionTagPayload,
} from 'entities/AgentActions'
import { SurveysApi } from 'entities/Surveys'
import { type IResponseFullSurvey } from 'entities/Surveys/api/types'
import { resolveSurveyBodyAttachments } from 'pages/surveys/lib/reconstructSurveyAttachments'

export type IDetailsSurveyTabKey =
  | 'contactEnrollment'
  | 'scoreElaboration'
  | 'closingMessage'
  | 'actions'

export type IDetailsMessageItem = {
  key: string
  label: string
  message: string
  attachments: Attachment[]
}

export type IDetailsActionGroup = {
  key: string
  label: string
  actions: IAgentActionSettings[] | null
}

type IMessageValue = { message: string; media_url: RequestAttachment[] }

export class DetailsSurveyModalStore {
  loading = true
  private _data: IResponseFullSurvey | null = null
  private _futureActionsTags = new Map<string, Promise<Tag[]>>()
  private _bodyAttachments = new Map<string, Attachment[]>()
  sendFromInfo: ISendFromInfo | null = null
  activeTabKey: IDetailsSurveyTabKey = 'contactEnrollment'

  constructor(private _surveyId: number, readonly entities: IEntitiesStore) {
    makeAutoObservable(this)

    void this._init()
  }

  get tabs(): ITabItem<IDetailsSurveyTabKey>[] {
    return [
      { key: 'contactEnrollment', name: 'Contact enrollment' },
      { key: 'scoreElaboration', name: 'Score elaboration' },
      { key: 'closingMessage', name: 'Closing message' },
      { key: 'actions', name: 'Actions' },
    ]
  }

  get npsQuestion() {
    const company = this._data?.question?.company_name?.trim() ?? 'company or product name'
    return `How likely are you to recommend '${company}' to your friends or colleagues? From 0 (not likely) to 10 (very likely)`
  }

  get contactsCount() {
    return this._data?.total_contacts_count ?? 0
  }

  get successActions(): IAgentActionSettings[] | null {
    return this._data?.settings?.success_action?.value ?? null
  }

  get isSuccessActionPerGroup() {
    return Boolean(this._data?.settings?.success_action_per_group?.value)
  }

  get successActionGroups(): IDetailsActionGroup[] {
    const settings = this._data?.settings
    return [
      {
        key: 'promoters',
        label: 'Promoters (9-10)',
        actions: settings?.success_action_promoters?.value ?? null,
      },
      {
        key: 'passives',
        label: 'Passives (7-8)',
        actions: settings?.success_action_passives?.value ?? null,
      },
      {
        key: 'detractors',
        label: 'Detractors (0-6)',
        actions: settings?.success_action_detractors?.value ?? null,
      },
    ]
  }

  get fallbackActions(): IAgentActionSettings[] | null {
    return this._data?.settings?.fallback_action?.value ?? null
  }

  get isScoreElaborationEnabled() {
    return Boolean(this._data?.settings?.score_elaboration?.value)
  }

  get isClosingMessageEnabled() {
    return Boolean(this._data?.settings?.closing_message?.value)
  }

  get closingMessageItems(): IDetailsMessageItem[] {
    const settings = this._data?.settings

    const groups = [
      {
        key: 'promoters',
        label: 'Promoters (9-10)',
        value: settings?.closing_message_promoters?.value,
      },
      {
        key: 'passives',
        label: 'Passives (7-8)',
        value: settings?.closing_message_passives?.value,
      },
      {
        key: 'detractors',
        label: 'Detractors (0-6)',
        value: settings?.closing_message_detractors?.value,
      },
    ]

    if (settings?.closing_message_per_group?.value) {
      return groups.map((group) =>
        this._buildMessageItem('closingMessage', group.key, group.label, group.value)
      )
    }

    return [
      this._buildMessageItem(
        'closingMessage',
        'message',
        'Message',
        settings?.closing_message_value?.value
      ),
    ]
  }

  get scoreElaborationItems(): IDetailsMessageItem[] {
    const settings = this._data?.settings

    const groups = [
      {
        key: 'promoters',
        label: 'Promoters (9-10)',
        value: settings?.score_elaboration_promoters?.value,
      },
      {
        key: 'passives',
        label: 'Passives (7-8)',
        value: settings?.score_elaboration_passives?.value,
      },
      {
        key: 'detractors',
        label: 'Detractors (0-6)',
        value: settings?.score_elaboration_detractors?.value,
      },
    ]

    if (settings?.score_elaboration_per_group?.value) {
      return groups.map((group) =>
        this._buildMessageItem('scoreElaboration', group.key, group.label, group.value)
      )
    }

    return [
      this._buildMessageItem(
        'scoreElaboration',
        'message',
        'Message',
        settings?.score_elaboration_message?.value
      ),
    ]
  }

  private _buildMessageItem = (
    section: string,
    key: string,
    label: string,
    value: IMessageValue | null | undefined
  ): IDetailsMessageItem => {
    const mediaAttachments = (value?.media_url ?? []).map(
      (requestAttachment) => new Attachment({ requestAttachment, fromMediaUrl: true })
    )
    const bodyAttachments = this._bodyAttachments.get(`${section}:${key}`) ?? []

    return {
      key,
      label,
      message: value?.message?.trim() ?? '',
      attachments: [...mediaAttachments, ...bodyAttachments],
    }
  }

  setActiveTab = (tab: ITabItem<IDetailsSurveyTabKey>) => {
    this.activeTabKey = tab.key
  }

  getFutureActionsTags = (ids: number[]) => {
    const key = ids.join(',')
    return this._futureActionsTags.get(key) ?? Promise.resolve([])
  }

  private _init = async () => {
    try {
      const { data } = await SurveysApi.getSurvey(this._surveyId)
      runInAction(() => {
        this._data = data
        this._initActionTags()
        this._restoreBodyAttachments()
      })

      if (data.send_from) {
        const info = await fetchSendFromInfo(data.send_from, this.entities)
        runInAction(() => {
          this.sendFromInfo = info
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  private _restoreBodyAttachments = () => {
    const settings = this._data?.settings
    if (!settings) return

    const items: Array<{ mapKey: string; value: IMessageValue | undefined }> = []
    const push = (section: string, key: string, value: IMessageValue | undefined | null) => {
      if (value) items.push({ mapKey: `${section}:${key}`, value })
    }

    if (settings.score_elaboration_per_group?.value) {
      push('scoreElaboration', 'promoters', settings.score_elaboration_promoters?.value)
      push('scoreElaboration', 'passives', settings.score_elaboration_passives?.value)
      push('scoreElaboration', 'detractors', settings.score_elaboration_detractors?.value)
    } else {
      push('scoreElaboration', 'message', settings.score_elaboration_message?.value)
    }

    if (settings.closing_message_per_group?.value) {
      push('closingMessage', 'promoters', settings.closing_message_promoters?.value)
      push('closingMessage', 'passives', settings.closing_message_passives?.value)
      push('closingMessage', 'detractors', settings.closing_message_detractors?.value)
    } else {
      push('closingMessage', 'message', settings.closing_message_value?.value)
    }

    items.forEach(({ mapKey, value }) => {
      if (!value) return
      void resolveSurveyBodyAttachments(
        this.entities,
        value.message ?? '',
        value.media_url ?? []
      ).then((attachments) => {
        if (attachments.length) {
          runInAction(() => {
            this._bodyAttachments.set(mapKey, attachments)
          })
        }
      })
    })
  }

  private _initActionTags = () => {
    const allActions = [
      ...(this.isSuccessActionPerGroup
        ? this.successActionGroups.flatMap((group) => group.actions ?? [])
        : this.successActions ?? []),
      ...(this.fallbackActions ?? []),
    ]

    allActions
      .filter(
        (action) =>
          action.type === AgentActionType.AddTag || action.type === AgentActionType.RemoveTag
      )
      .map((action) => action.settings as IAgentActionTagPayload)
      .forEach(({ tag_ids: ids }) => {
        const key = ids.join(',')
        if (this._futureActionsTags.has(key)) return
        const futureTags = TagsApi.getTagsById(ids).then(
          (tags) => tags.map((tag) => new Tag(tag)),
          () => []
        )
        this._futureActionsTags.set(key, futureTags)
      })
  }
}
